<template>
  <Filestack
    ref="filestackComponent"
    :apikey="apiKey"
    :options="options"
    :security="internalSecurity"
    :refreshSecurity="refreshSecurity"
    :refreshSecurityForFile="refreshSecurityForFile"
    :invalidateSecurity="invalidateSecurity"
    @init="event => $emit('init', event)"
    @error="event => $emit('error', event)"
    @success="event => $emit('success', event)"
  >
    <slot></slot>
  </Filestack>
</template>

<script>
import logger from '@goldcast/logger';

import Filestack from './Filestack.vue';
import { uuidGen, FILESTACK_DEFAULT_CUSTOM_TEXT } from './utils';

import { getFilestackSecurity, isFilestackSecurityError } from '@/components/core/Filestack/filestackSecurity';
import { getEnv } from '@/utils/env';

export default {
  name: 'FilestackS3',
  components: {
    Filestack
  },
  props: {
    acceptedTypes: {
      type: Array,
      default() {
        return [];
      }
    },
    s3path: {
      type: String,
      default: ''
    },
    renameFileNameToUUID: {
      type: Boolean,
      default: false
    },
    replaceSpacesInFileName: {
      type: Boolean,
      default: false
    },
    customFilePreprocess: {
      type: Boolean,
      default: false
    },
    customFilePreprocessFun: {
      type: Function,
      default: () => ({})
    },
    disableStorageKey: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 50 * 1024 * 1024 // change default size to 50MB, it used for images
    },
    allowManualRetry: {
      type: Boolean,
      default: true
    },
    container: {
      type: String,
      default: null
    },
    customText: {
      type: Object,
      default: null
    },
    errorsTimeout: {
      type: Number, // time in mili seconds.
      default: null
    },
    imageValidation: {
      type: Object,
      default: () => {
        return {
          validationType: null,
          desiredImageRatio: null,
          desiredImageRatioRange: null,
          imageMaxWidth: null,
          imageMaxHeight: null,
          validationErrorMessage: null
        };
      }
    },
    maxFiles: { type: Number, default: 1 },
    filestackIntent: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      internalSecurity: null,
      baseOptions: {
        fromSources: ['local_file_system'],
        storeTo: {
          location: 's3',
          container: getEnv('VUE_APP_S3_BUCKET'),
          path: this.s3path,
          region: getEnv('VUE_APP_AWS_STORE_REGION')
        },
        uploadConfig: {
          retry: 3,
          timeout: 60000
        },
        uploadInBackground: false,
        onFileUploadFailed: err => {
          this.$emit('error', err);
        },
        disableTransformer: true,
        disableAltText: true,
        maxFiles: 1,
        minFiles: 1,
        exposeOriginalFile: true,
        onUploadDone: files => {
          this.$emit('fileuploaded', files);
        },
        onFileSelected: this.postProcessFileSelection,
        onCancel: () => {
          this.$emit('cancelUpload');
        },
        onClose: () => {
          this.$emit('close');
        },
        onOpen: pickerInstance => {
          this.$emit('onOpen', pickerInstance);
        },
        disableStorageKey: false,
        maxSize: 50 * 1024 * 1024, // change default size to 50MB, it used for images,
        customText: FILESTACK_DEFAULT_CUSTOM_TEXT
      },
      apiKey: getEnv('VUE_APP_FILESTACK_KEY')
    };
  },
  computed: {
    shouldSignUpload() {
      return Boolean(this.filestackIntent);
    },
    options() {
      const storeTo = {
        ...this.baseOptions.storeTo,
        path: this.s3path
      };
      const options = {
        ...this.baseOptions,
        storeTo
      };
      options['allowManualRetry'] = this.allowManualRetry;

      if (this.isValidArray(this.acceptedTypes)) {
        options['accept'] = this.acceptedTypes;
      }
      if (this.disableStorageKey) {
        options['disableStorageKey'] = this.disableStorageKey;
      }
      if (this.maxSize) {
        options['maxSize'] = this.maxSize;
      }
      if (this.container) {
        options['displayMode'] = 'inline';
        options['container'] = this.container;
      }
      if (this.customText) {
        options['customText'] = this.customText;
      }
      if (this.errorsTimeout) {
        options['errorsTimeout'] = this.errorsTimeout;
      }
      if (this.maxFiles) {
        options['maxFiles'] = this.maxFiles;
      }
      return options;
    }
  },
  methods: {
    async refreshSecurity(sizeBytes) {
      if (!this.shouldSignUpload) {
        this.internalSecurity = null;
        return null;
      }
      try {
        const { security } = await getFilestackSecurity(this.filestackIntent, { sizeBytes });
        this.internalSecurity = security;
        return security;
      } catch (e) {
        logger.error('[FilestackS3] Failed to fetch Filestack security, upload blocked', e);
        this.$emit('error', e);
        throw e;
      }
    },
    refreshSecurityForFile(file) {
      return this.refreshSecurity(file?.size);
    },
    invalidateSecurity(error) {
      if (!this.shouldSignUpload || !isFilestackSecurityError(error)) {
        return;
      }
      this.internalSecurity = null;
    },
    /* eslint-disable */
    closePicker() {
      this.$emit('close');
      this.$refs.filestackComponent?.picker?.close();
    },
    /* eslint-enable */
    isValidArray(items) {
      return Array.isArray(items) && items.length;
    },
    extractRatioFromString(ratioString) {
      const ratios = ratioString.split(':');
      return (parseInt(ratios[0]) / parseInt(ratios[1])).toFixed(3);
    },
    imageNativeLoader(file) {
      const blob = file.originalFile;
      return {
        url: URL.createObjectURL(blob),
        img: new Image(),
        validationErrorMessage: this.imageValidation.validationErrorMessage,
        validateImageSize: this.validateImageSize
      };
    },
    validateImageRatioRange(file, resolve, reject) {
      const desiredMinRatio = this.extractRatioFromString(this.imageValidation.desiredImageRatioRange?.min || '');
      const desiredMaxRatio = this.extractRatioFromString(this.imageValidation.desiredImageRatioRange?.max || '');

      const { img, url, validateImageSize, validationErrorMessage } = this.imageNativeLoader(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        const imageRatio = (this.naturalWidth / this.naturalHeight).toFixed(3);
        const isValidRatio = imageRatio >= desiredMinRatio && imageRatio <= desiredMaxRatio;
        const isValidImageSize = validateImageSize(this.naturalWidth, this.naturalHeight);
        if (!isValidRatio || !isValidImageSize) {
          reject(validationErrorMessage);
        }
        resolve();
      };
      img.src = url;
    },
    validateImageDimensions(file, resolve, reject) {
      const { img, url, validateImageSize, validationErrorMessage } = this.imageNativeLoader(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        const isValidImageSize = validateImageSize(this.naturalWidth, this.naturalHeight);
        if (!isValidImageSize) {
          reject(validationErrorMessage);
        }
        resolve();
      };
      img.src = url;
    },
    validateImageSize(naturalWidth, naturalHeight) {
      let isValid = true;
      if (this.imageValidation.imageMaxWidth && naturalWidth > this.imageValidation.imageMaxWidth) {
        isValid = false;
      }

      if (this.imageValidation.imageMaxHeight && naturalHeight > this.imageValidation.imageMaxHeight) {
        isValid = false;
      }
      return isValid;
    },
    validateImageRatio(file, resolve, reject) {
      const desiredRatio = this.extractRatioFromString(this.imageValidation?.desiredImageRatio || '');
      const { img, url, validateImageSize, validationErrorMessage } = this.imageNativeLoader(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        const imageRatio = (this.naturalWidth / this.naturalHeight).toFixed(3);
        const isValidImageSize = validateImageSize(this.naturalWidth, this.naturalHeight);
        if (imageRatio !== desiredRatio || !isValidImageSize) {
          reject(validationErrorMessage);
        }

        resolve();
      };
      img.src = url;
    },
    validateImageFile(file) {
      return new Promise((resolve, reject) => {
        if (this.imageValidation.desiredImageRatio) {
          this.validateImageRatio(file, resolve, reject);
        } else if (this.imageValidation.desiredImageRatioRange) {
          this.validateImageRatioRange(file, resolve, reject);
        } else {
          this.validateImageDimensions(file, resolve, reject);
        }
      });
    },
    changeFileNameToUUID(file) {
      const fileName = file.filename;
      const extension = (fileName || '').split('.').pop();
      return {
        ...file,
        name: `${uuidGen()}.${extension}`
      };
    },
    replaceSpacesInFileNameFn(file) {
      const fileName = file.filename || '';
      const extension = fileName.includes('.') ? '.' + fileName.split('.').pop() : '';
      const cleanFileName = fileName.trim().replace(/\s+/g, '_').replace(/’/g, '').replace(/'/g, '');
      const isInvalid = !cleanFileName || cleanFileName === extension;
      return {
        ...file,
        name: isInvalid ? `file${extension}` : cleanFileName
      };
    },
    async postProcessFileSelection(file) {
      this.$emit('onFileSelected', file);
      if (this.customFilePreprocess) {
        const alteredFile = await this.customFilePreprocessFun(file);
        return alteredFile;
      }
      return new Promise((resolve, reject) => {
        let validationPromise = Promise.resolve();
        if (file.mimetype === 'image/svg+xml') {
          const error = 'SVG files are not allowed.';
          this.$emit('error', error);
          reject(error);
        }

        if (this.imageValidation.validationType === 'image') {
          validationPromise = this.validateImageFile(file);
        }

        validationPromise
          .then(() => {
            let processedFile = file;
            if (this.replaceSpacesInFileName) {
              processedFile = this.replaceSpacesInFileNameFn(processedFile);
            }
            if (this.renameFileNameToUUID) {
              processedFile = this.changeFileNameToUUID(processedFile);
            }

            return resolve(processedFile);
          })
          .catch(reject);
      });
    }
  }
};
</script>

<style>
.fsp-summary__filter > input {
  display: none;
}

.fsp-summary__item-name > span {
  color: #444444;
}

#__filestack-picker .fsp-picker__brand-container {
  display: none;
}
</style>
